import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Input, Modal, Popconfirm, Select, Space, Table, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { baseURL } from '../API/url';
import {
  CatergoryLinkModal,
  RecordField,
  RecordFormModal,
  compareText,
  errorMessage,
  renderCatergory,
} from './managementShared';

export interface ManagedAdvisor {
  recId: string;
  emdId?: string | null;
  emdFirm?: string | null;
  categoryRecId?: string | null;
  categoryDescription?: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  mobilePhone: string | null;
}

interface EmdOption {
  recId: string;
  firm: string | null;
  contactFirstname: string | null;
  contactLastname: string | null;
}

interface AdvisorManagementModalProps {
  open: boolean;
  onClose: () => void;
}

const ADVISOR_FIELDS: RecordField[] = [
  { name: 'firstName', label: 'First Name', required: true },
  { name: 'lastName', label: 'Last Name', required: true },
  { name: 'email', label: 'Email', email: true },
  { name: 'phone', label: 'Phone' },
  { name: 'mobilePhone', label: 'Mobile Phone' },
];

export const advisorName = (advisor: ManagedAdvisor) =>
  advisor.fullName || [advisor.firstName, advisor.lastName].filter(Boolean).join(' ');

export const emdLabel = (emd: EmdOption) => {
  const contact = [emd.contactFirstname, emd.contactLastname].filter(Boolean).join(' ');
  return contact ? `${emd.firm || '(no firm)'} — ${contact}` : emd.firm || '(no firm)';
};

const AdvisorManagementModal: React.FC<AdvisorManagementModalProps> = ({ open, onClose }) => {
  const [advisors, setAdvisors] = useState<ManagedAdvisor[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [editing, setEditing] = useState<ManagedAdvisor | 'new' | null>(null);
  const [saving, setSaving] = useState(false);

  const [catergoryTarget, setCatergoryTarget] = useState<ManagedAdvisor | null>(null);
  const [savingCatergory, setSavingCatergory] = useState(false);

  const [emdTarget, setEmdTarget] = useState<ManagedAdvisor | null>(null);
  const [emds, setEmds] = useState<EmdOption[]>([]);
  const [loadingEmds, setLoadingEmds] = useState(false);
  const [selectedEmd, setSelectedEmd] = useState<string | null>(null);
  const [savingEmd, setSavingEmd] = useState(false);

  const loadAdvisors = useCallback(async (term: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/advisor-management`, {
        params: term ? { search: term } : {},
      });
      setAdvisors(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to load advisors.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearch('');
      loadAdvisors('');
    }
  }, [open, loadAdvisors]);

  const replaceRow = (updated: ManagedAdvisor) =>
    setAdvisors((rows) => rows.map((row) => (row.recId === updated.recId ? updated : row)));

  const handleSave = async (values: Record<string, any>) => {
    setSaving(true);
    try {
      if (editing === 'new') {
        const response = await axios.post(`${baseURL}/advisor-management`, values);
        setAdvisors((rows) => [response.data, ...rows]);
        message.success('Advisor created.');
      } else if (editing) {
        const response = await axios.put(`${baseURL}/advisor-management/${editing.recId}`, values);
        replaceRow(response.data);
        message.success('Advisor updated.');
      }
      setEditing(null);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to save the advisor.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (advisor: ManagedAdvisor) => {
    try {
      await axios.delete(`${baseURL}/advisor-management/${advisor.recId}`);
      setAdvisors((rows) => rows.filter((row) => row.recId !== advisor.recId));
      message.success(`${advisorName(advisor) || 'Advisor'} deleted.`);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to delete the advisor.'));
    }
  };

  const handleSaveCatergory = async (categoryRecId: string | null) => {
    if (!catergoryTarget) return;
    setSavingCatergory(true);
    try {
      const response = await axios.put(
        `${baseURL}/advisor-management/${catergoryTarget.recId}/catergory`,
        { categoryRecId },
      );
      replaceRow(response.data);
      message.success(categoryRecId ? 'Catergory linked.' : 'Catergory unlinked.');
      setCatergoryTarget(null);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to link the catergory.'));
    } finally {
      setSavingCatergory(false);
    }
  };

  const openEmdLink = async (advisor: ManagedAdvisor) => {
    setEmdTarget(advisor);
    setSelectedEmd(advisor.emdId || null);
    setLoadingEmds(true);
    try {
      const response = await axios.get(`${baseURL}/emd/management`);
      setEmds(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to load EMDs.'));
    } finally {
      setLoadingEmds(false);
    }
  };

  const handleSaveEmd = async () => {
    if (!emdTarget) return;
    if (selectedEmd === (emdTarget.emdId || null)) {
      setEmdTarget(null);
      return;
    }
    setSavingEmd(true);
    try {
      const response = await axios.put(`${baseURL}/advisor-management/${emdTarget.recId}/emd`, {
        emdId: selectedEmd,
      });
      replaceRow(response.data.advisor);
      message.success(selectedEmd ? 'Advisor linked to EMD.' : 'Advisor unlinked from EMD.');
      setEmdTarget(null);
    } catch (error) {
      message.error(errorMessage(error, 'Unable to link the EMD.'));
    } finally {
      setSavingEmd(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      key: 'name',
      render: (_: unknown, record: ManagedAdvisor) => advisorName(record),
      sorter: (a: ManagedAdvisor, b: ManagedAdvisor) => compareText(advisorName(a), advisorName(b)),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Mobile', dataIndex: 'mobilePhone', key: 'mobilePhone' },
    {
      title: 'EMD',
      key: 'emd',
      render: (_: unknown, record: ManagedAdvisor) =>
        record.emdFirm || <Typography.Text type="secondary">—</Typography.Text>,
      sorter: (a: ManagedAdvisor, b: ManagedAdvisor) => compareText(a.emdFirm, b.emdFirm),
    },
    {
      title: 'Catergory',
      key: 'catergory',
      render: (_: unknown, record: ManagedAdvisor) => renderCatergory(record),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: ManagedAdvisor) => (
        <Space size="small" wrap>
          <Button size="small" onClick={() => setEditing(record)}>
            Edit
          </Button>
          <Button size="small" onClick={() => setCatergoryTarget(record)}>
            Link Catergory
          </Button>
          <Button size="small" onClick={() => openEmdLink(record)}>
            Link EMD
          </Button>
          <Popconfirm
            title="Delete this advisor?"
            description="This cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record)}
          >
            <Button size="small" danger>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Advisor Management"
        open={open}
        onCancel={onClose}
        footer={<Button onClick={onClose}>Close</Button>}
        width={1200}
      >
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Input.Search
            placeholder="Search name, email or firm"
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={(value) => loadAdvisors(value.trim())}
            style={{ width: 360 }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing('new')}>
            Add Advisor
          </Button>
        </Space>
        <Table
          rowKey="recId"
          size="small"
          loading={loading}
          dataSource={advisors}
          columns={columns}
          pagination={{ pageSize: 20, showSizeChanger: false }}
        />
      </Modal>

      {editing && (
        <RecordFormModal
          title={editing === 'new' ? 'Add Advisor' : 'Edit Advisor'}
          fields={ADVISOR_FIELDS}
          initialValues={
            editing === 'new'
              ? {}
              : Object.fromEntries(
                  ADVISOR_FIELDS.map((f) => [f.name, editing[f.name as keyof ManagedAdvisor] || '']),
                )
          }
          saving={saving}
          onCancel={() => setEditing(null)}
          onSubmit={handleSave}
        />
      )}

      <CatergoryLinkModal
        title={`Link Catergory — ${catergoryTarget ? advisorName(catergoryTarget) : ''}`}
        open={!!catergoryTarget}
        currentRecId={catergoryTarget?.categoryRecId}
        saving={savingCatergory}
        onCancel={() => setCatergoryTarget(null)}
        onSave={handleSaveCatergory}
      />

      <Modal
        title={`Link EMD — ${emdTarget ? advisorName(emdTarget) : ''}`}
        open={!!emdTarget}
        onCancel={() => setEmdTarget(null)}
        onOk={handleSaveEmd}
        okText="Save"
        confirmLoading={savingEmd}
        destroyOnHidden
      >
        <Select
          aria-label="EMD"
          style={{ width: '100%' }}
          placeholder="Select an EMD (clear to unlink)"
          loading={loadingEmds}
          allowClear
          showSearch
          optionFilterProp="label"
          value={selectedEmd ?? undefined}
          onChange={(value) => setSelectedEmd(value ?? null)}
          options={emds.map((emd) => ({ value: emd.recId, label: emdLabel(emd) }))}
        />
      </Modal>
    </>
  );
};

export default AdvisorManagementModal;
